/**
 * FJOR welcome-1 interactive advertorial
 * Reading progress, chapter reveals, formula nodes, 90-day scrubber,
 * vitality dial, offer tiers, review carousel, FAQ accordion, sticky CTA.
 * Cost bars animate via chapter is-inview class (CSS only).
 * Build: 2026-07-22-welcome-1-v1
 */
(function () {
  const root = document.querySelector('[data-fjor-welcome]');
  if (!root) return;

  const productUrl = root.getAttribute('data-product-url') || '/products/inner-warrior';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Ingredient panel data ─────────────────────────────────────── */
  const NODES = {
    tongkat: {
      tier: 'Lead driver',
      title: 'Tongkat Ali',
      copy: 'Supports free testosterone and resilience under stress. The front line of the stack.',
      meter: 92,
    },
    fadogia: {
      tier: 'Lead driver',
      title: 'Fadogia Agrestis',
      copy: 'Supports vitality, strength signaling, and drive. Paired with Tongkat for the core effect.',
      meter: 88,
    },
    turk: {
      tier: 'Lead driver',
      title: 'Turkesterone',
      copy: 'Non-hormonal muscle support used by serious trainers. Completes the lead trio.',
      meter: 84,
    },
    zinc: {
      tier: 'Core support',
      title: 'Zinc',
      copy: 'A mineral tied to hormone metabolism that most men underdose or skip.',
      meter: 68,
    },
    mag: {
      tier: 'Core support',
      title: 'Magnesium',
      copy: 'Supports deep recovery sleep. The nights are where the protocol does its work.',
      meter: 64,
    },
    d3: {
      tier: 'Foundation',
      title: 'Vitamin D3',
      copy: 'The quiet foundation the rest of the stack leans on. Most men run low.',
      meter: 52,
    },
  };

  const DAYS = [
    { at: 1, title: 'First serving', copy: 'Three capsules with food. Nothing dramatic happens today. That is normal and by design.' },
    { at: 21, title: 'The habit locks', copy: 'Three weeks in. The routine runs itself now. Sleep and training are usually the first things to steady.' },
    { at: 45, title: 'The signal builds', copy: 'Halfway. Energy and drive start to compound. Often someone at home notices before you do. Judge it on your mornings.' },
    { at: 70, title: 'Momentum', copy: 'Recovery holds under harder training. The week costs less than it used to. This is where staying on protocol matters most.' },
    { at: 90, title: 'Decision point', copy: 'A full quarter of data, guarantee behind you. Keep going, adjust, or take the refund. Either way you know.' },
  ];

  const ECHO = {
    low: {
      quote: 'You said: dragging most days, mornings you barely recognize.',
      window: 'Most men in this range have felt it for a while, not a week. They give it 60 to 90 days and judge it on mornings.',
      cta: 'See what is in it',
    },
    mid: {
      quote: 'You said: training holds, but you feel drive and recovery slipping.',
      window: 'You caught it early. Most men in this range feel the shift in the 30 to 60 day window.',
      cta: 'See what is in it',
    },
    high: {
      quote: 'You said: still sharp, the slide has not started.',
      window: 'Your baseline looks strong. Protect it or skip it; the 90-day window works either way.',
      cta: 'Protect the baseline',
    },
  };

  /* ── Reading progress + sticky ─────────────────────────────────── */
  const progressEl = root.querySelector('[data-fjw-progress]');
  const sticky = root.querySelector('[data-fjw-sticky]');
  const hero = root.querySelector('.fjw-hero');
  const offerSection = root.querySelector('#offer');

  /* ── Scroll-turn jar (3 real label faces) ──────────────────────── */
  const turnStage = root.querySelector('[data-fjw-turn-stage]');
  const turnFaces = turnStage ? Array.prototype.slice.call(turnStage.querySelectorAll('[data-fjw-face]')) : [];
  const revealSection = root.querySelector('[data-fjw-chapter="reveal"]');
  const FACE_FADE = 0.12;

  function faceOpacity(p, fadeInAt, fadeOutAt) {
    let o = 1;
    if (fadeInAt !== null) o = Math.min(o, (p - fadeInAt) / FACE_FADE);
    if (fadeOutAt !== null) o = Math.min(o, (fadeOutAt - p) / FACE_FADE);
    return Math.max(0, Math.min(1, o));
  }

  function updateTurn() {
    if (!turnStage || !revealSection || turnFaces.length < 3 || reduceMotion) return;
    const r = revealSection.getBoundingClientRect();
    const vh = window.innerHeight;
    const p = Math.max(0, Math.min(1, (vh - r.top) / (r.height + vh)));
    turnFaces[0].style.opacity = String(faceOpacity(p, null, 0.45));
    turnFaces[1].style.opacity = String(faceOpacity(p, 0.33, 0.72));
    turnFaces[2].style.opacity = String(faceOpacity(p, 0.6, null));
    turnStage.style.transform = 'rotateY(' + ((p - 0.5) * 16).toFixed(2) + 'deg)';
  }

  function onScroll() {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const p = max > 0 ? (window.scrollY / max) * 100 : 0;
    if (progressEl) progressEl.style.width = p.toFixed(2) + '%';

    if (sticky && hero) {
      sticky.hidden = false;
      const pastHero = window.scrollY > hero.offsetHeight * 0.7;
      let overOffer = false;
      if (offerSection) {
        const r = offerSection.getBoundingClientRect();
        overOffer = r.top < window.innerHeight * 0.7 && r.bottom > 0;
      }
      sticky.classList.toggle('is-visible', pastHero && !overOffer);
    }

    updateTurn();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ── Chapter inview ────────────────────────────────────────────── */
  const chapters = root.querySelectorAll('[data-fjw-chapter]');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-inview');
        });
      },
      { threshold: 0.22 }
    );
    chapters.forEach((el) => io.observe(el));
  } else {
    chapters.forEach((el) => el.classList.add('is-inview'));
  }

  /* ── Formula nodes ─────────────────────────────────────────────── */
  const nodes = root.querySelectorAll('[data-fjw-node]');
  const panelTier = root.querySelector('[data-fjw-panel-tier]');
  const panelTitle = root.querySelector('[data-fjw-panel-title]');
  const panelCopy = root.querySelector('[data-fjw-panel-copy]');
  const panelMeter = root.querySelector('[data-fjw-panel-meter]');

  function setNode(id) {
    const data = NODES[id];
    if (!data) return;
    nodes.forEach((btn) => {
      const on = btn.getAttribute('data-fjw-node') === id;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (panelTier) panelTier.textContent = data.tier;
    if (panelTitle) panelTitle.textContent = data.title;
    if (panelCopy) panelCopy.textContent = data.copy;
    if (panelMeter) panelMeter.style.width = data.meter + '%';
  }

  nodes.forEach((btn) => {
    const id = btn.getAttribute('data-fjw-node');
    btn.addEventListener('click', () => setNode(id));
    btn.addEventListener('mouseenter', () => {
      if (window.matchMedia('(hover: hover)').matches) setNode(id);
    });
  });

  /* ── 90-day signal chart ───────────────────────────────────────── */
  const chartWrap = root.querySelector('[data-fjw-chart]');
  const CW = 600;
  const CH = 200;
  const CPX = 20;
  const CPTOP = 18;
  const CPBOT = 34;
  let chartMarker = null;
  let chartDot = null;
  let chartMilestones = [];

  function signalAt(t) {
    const s = t * t * (3 - 2 * t);
    return 0.1 + 0.82 * s;
  }

  function chartX(t) {
    return CPX + t * (CW - 2 * CPX);
  }

  function chartY(t) {
    return CPTOP + (1 - signalAt(t)) * (CH - CPTOP - CPBOT);
  }

  if (chartWrap) {
    const pts = [];
    for (let i = 0; i <= 45; i++) {
      const t = i / 45;
      pts.push(chartX(t).toFixed(1) + ',' + chartY(t).toFixed(1));
    }
    const line = 'M' + pts.join(' L');
    const area = line + ' L' + chartX(1).toFixed(1) + ',' + (CH - CPBOT) + ' L' + chartX(0).toFixed(1) + ',' + (CH - CPBOT) + ' Z';
    const MS = [
      { d: 21, label: 'Habit' },
      { d: 45, label: 'Signal' },
      { d: 90, label: 'Decision' },
    ];
    const msSvg = MS.map(function (m) {
      const t = (m.d - 1) / 89;
      const x = chartX(t).toFixed(1);
      const anchor = m.d === 90 ? 'end' : 'middle';
      return (
        '<circle data-ms="' + m.d + '" cx="' + x + '" cy="' + chartY(t).toFixed(1) + '" r="4"></circle>' +
        '<text x="' + x + '" y="' + (CH - CPBOT + 18) + '" text-anchor="' + anchor + '">Day ' + m.d + ' · ' + m.label + '</text>'
      );
    }).join('');
    chartWrap.innerHTML =
      '<svg viewBox="0 0 ' + CW + ' ' + CH + '" class="fjw-chart__svg" focusable="false">' +
      '<defs><linearGradient id="fjwChartFill" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#ffbf30" stop-opacity="0.3"></stop>' +
      '<stop offset="1" stop-color="#ffbf30" stop-opacity="0"></stop>' +
      '</linearGradient></defs>' +
      '<line class="fjw-chart__base" x1="' + CPX + '" y1="' + (CH - CPBOT) + '" x2="' + (CW - CPX) + '" y2="' + (CH - CPBOT) + '"></line>' +
      '<path class="fjw-chart__area" d="' + area + '"></path>' +
      '<path class="fjw-chart__line" d="' + line + '"></path>' +
      msSvg +
      '<line class="fjw-chart__marker" data-fjw-chart-marker x1="' + CPX + '" y1="' + CPTOP + '" x2="' + CPX + '" y2="' + (CH - CPBOT) + '"></line>' +
      '<circle class="fjw-chart__dot" data-fjw-chart-dot cx="' + CPX + '" cy="' + chartY(0).toFixed(1) + '" r="5"></circle>' +
      '</svg>';
    chartMarker = chartWrap.querySelector('[data-fjw-chart-marker]');
    chartDot = chartWrap.querySelector('[data-fjw-chart-dot]');
    chartMilestones = Array.prototype.slice.call(chartWrap.querySelectorAll('[data-ms]'));
  }

  /* ── 90-day protocol scrubber ──────────────────────────────────── */
  const dayRange = root.querySelector('[data-fjw-day-range]');
  const dayLabel = root.querySelector('[data-fjw-day]');
  const dayTitle = root.querySelector('[data-fjw-day-title]');
  const dayCopy = root.querySelector('[data-fjw-day-copy]');

  function nearestDay(n) {
    let best = DAYS[0];
    for (let i = 0; i < DAYS.length; i++) {
      if (n >= DAYS[i].at) best = DAYS[i];
    }
    return best;
  }

  function updateDay(n) {
    const day = nearestDay(n);
    if (dayLabel) dayLabel.textContent = 'Day ' + n;
    if (dayTitle) dayTitle.textContent = day.title;
    if (dayCopy) dayCopy.textContent = day.copy;

    const t = (n - 1) / 89;
    const x = chartX(t).toFixed(1);
    if (chartMarker) {
      chartMarker.setAttribute('x1', x);
      chartMarker.setAttribute('x2', x);
    }
    if (chartDot) {
      chartDot.setAttribute('cx', x);
      chartDot.setAttribute('cy', chartY(t).toFixed(1));
    }
    chartMilestones.forEach(function (c) {
      c.classList.toggle('is-passed', n >= Number(c.getAttribute('data-ms')));
    });
  }

  if (dayRange) {
    dayRange.addEventListener('input', () => updateDay(Number(dayRange.value)));
    updateDay(Number(dayRange.value));
  }

  /* ── Vitality dial ─────────────────────────────────────────────── */
  const dialRange = root.querySelector('[data-fjw-dial-range]');
  const dialArc = root.querySelector('[data-fjw-dial-arc]');
  const dialScore = root.querySelector('[data-fjw-dial-score]');
  const dialLabel = root.querySelector('[data-fjw-dial-label]');
  const signalItems = root.querySelectorAll('[data-signal]');
  const echoQuote = root.querySelector('[data-fjw-echo-quote]');
  const echoWindow = root.querySelector('[data-fjw-echo-window]');
  const echoCta = root.querySelector('[data-fjw-echo-cta]');
  const CIRC = 2 * Math.PI * 78;

  function updateDial(v) {
    const clamped = Math.max(0, Math.min(100, v));
    if (dialScore) dialScore.textContent = String(clamped);
    if (dialArc) dialArc.style.strokeDashoffset = String(CIRC * (1 - clamped / 100));

    let key = 'mid';
    let label = 'Showing wear';
    if (clamped < 40) {
      key = 'low';
      label = 'Significant signals';
    } else if (clamped >= 75) {
      key = 'high';
      label = 'Strong baseline';
    }
    if (dialLabel) dialLabel.textContent = label;
    signalItems.forEach((li) => {
      li.classList.toggle('is-active', li.getAttribute('data-signal') === key);
    });

    const echo = ECHO[key];
    if (echo) {
      if (echoQuote) echoQuote.textContent = echo.quote;
      if (echoWindow) echoWindow.textContent = echo.window;
      if (echoCta) echoCta.textContent = echo.cta;
    }
  }

  if (dialRange) {
    dialRange.addEventListener('input', () => updateDial(Number(dialRange.value)));
    updateDial(Number(dialRange.value));
  }

  /* ── Offer tiers ───────────────────────────────────────────────── */
  const tiers = root.querySelectorAll('[data-fjw-tier]');
  const offerCta = root.querySelector('[data-fjw-offer-cta]');
  const sellingPlanInput = root.querySelector('[data-fjw-selling-plan]');
  const orderSummary = root.querySelector('[data-fjw2-summary]');
  const ctaVerb = (offerCta && offerCta.getAttribute('data-fjw-cta-verb')) || 'Add to Cart';

  function setTier(id) {
    tiers.forEach((btn) => {
      const on = btn.getAttribute('data-fjw-tier') === id;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // Subscribe submits with the selling plan; one-time submits without it.
    if (sellingPlanInput) sellingPlanInput.disabled = id !== 'sub';
    if (offerCta) {
      offerCta.textContent = id === 'sub' ? ctaVerb + ' · Subscribe $29.99/mo' : ctaVerb + ' · One-time $39.99';
      if (offerCta.tagName === 'A') offerCta.setAttribute('href', productUrl);
    }
    if (orderSummary) {
      orderSummary.textContent = id === 'sub' ? '1 × Inner Warrior · Subscribe · $29.99/mo' : '1 × Inner Warrior · One-time · $39.99';
    }
  }

  tiers.forEach((btn) => {
    btn.addEventListener('click', () => setTier(btn.getAttribute('data-fjw-tier')));
  });

  /* ── Reviews ───────────────────────────────────────────────────── */
  const reviews = Array.from(root.querySelectorAll('.fjw-review'));
  const dots = Array.from(root.querySelectorAll('[data-fjw-review-dots] button'));
  let reviewIndex = 0;
  let reviewTimer;

  function showReview(i) {
    reviewIndex = (i + reviews.length) % reviews.length;
    reviews.forEach((el, idx) => el.classList.toggle('is-active', idx === reviewIndex));
    dots.forEach((el, idx) => el.classList.toggle('is-active', idx === reviewIndex));
  }

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => {
      showReview(i);
      restartReviews();
    });
  });

  function restartReviews() {
    clearInterval(reviewTimer);
    if (reduceMotion || reviews.length < 2) return;
    reviewTimer = setInterval(() => showReview(reviewIndex + 1), 5200);
  }

  restartReviews();

  /* ── UGC proof rail: tap to play with sound, one at a time ─────── */
  const proofCards = Array.prototype.slice.call(root.querySelectorAll('.fjw-proof__card'));
  proofCards.forEach((card) => {
    const video = card.querySelector('[data-fjw-proof-video]');
    const btn = card.querySelector('[data-fjw-proof-toggle]');
    if (!video || !btn) return;

    function stop() {
      video.pause();
      card.classList.remove('is-playing');
    }

    function toggle() {
      if (video.paused) {
        proofCards.forEach((other) => {
          if (other !== card) {
            const v = other.querySelector('[data-fjw-proof-video]');
            if (v) v.pause();
            other.classList.remove('is-playing');
          }
        });
        video.muted = false;
        video.play();
        card.classList.add('is-playing');
      } else {
        stop();
      }
    }

    btn.addEventListener('click', toggle);
    video.addEventListener('click', toggle);
    video.addEventListener('pause', () => card.classList.remove('is-playing'));
  });

  /* ── Rail arrows (shared by proof + saying rails) ──────────────── */
  Array.prototype.slice.call(root.querySelectorAll('.fjw-rail-shell')).forEach((shell) => {
    const rail = shell.querySelector('.fjw-rail');
    const prev = shell.querySelector('[data-fjw-rail-prev]');
    const next = shell.querySelector('[data-fjw-rail-next]');
    if (!rail || !prev || !next) return;

    function step() {
      const card = rail.firstElementChild;
      return card ? card.getBoundingClientRect().width + 12 : 240;
    }

    function updateArrows() {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      prev.classList.toggle('is-hidden', rail.scrollLeft <= 2);
      next.classList.toggle('is-hidden', rail.scrollLeft >= max);
    }

    prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    rail.addEventListener('scroll', updateArrows, { passive: true });
    rail.addEventListener('dragstart', (e) => e.preventDefault());

    function centerRail() {
      const fits = rail.scrollWidth <= rail.clientWidth + 2;
      rail.classList.toggle('is-centered', fits);
      updateArrows();
      prev.style.display = fits ? 'none' : '';
      next.style.display = fits ? 'none' : '';
    }

    centerRail();
    // Desktop: start overflowing rails at their midpoint for symmetric peeks
    if (window.matchMedia('(min-width: 768px)').matches && !rail.classList.contains('is-centered')) {
      rail.scrollLeft = (rail.scrollWidth - rail.clientWidth) / 2;
    }
    window.addEventListener('resize', centerRail);
  });

  /* ── Full label modal ──────────────────────────────────────────── */
  const labelModal = root.querySelector('[data-fjw-labelmodal]');
  if (labelModal) {
    const openers = root.querySelectorAll('[data-fjw-label-open]');
    const closers = labelModal.querySelectorAll('[data-fjw-label-close]');

    function setLabelModal(open) {
      labelModal.hidden = !open;
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }

    openers.forEach((btn) => btn.addEventListener('click', () => setLabelModal(true)));
    closers.forEach((el) => el.addEventListener('click', () => setLabelModal(false)));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !labelModal.hidden) setLabelModal(false);
    });
  }

  /* ── FAQ accordion (one open at a time) ────────────────────────── */
  const faqWrap = root.querySelector('[data-fjw-faq]');
  if (faqWrap) {
    const items = Array.from(faqWrap.querySelectorAll('details'));
    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
  }
})();
